import { useEffect, useRef, useState } from 'react';
import { apiSearchAirlines, type AirlineDTO } from '../api';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onAlliance: (a: string) => void;
  datalistId: string;
}

/**
 * Airline company autocomplete for loyalty-program rows. Debounces an airline
 * search (apiSearchAirlines → seeded catalog with alliance) and, when the typed
 * value exactly matches a member airline, auto-fills the alliance via onAlliance.
 */
export default function AirlineCompanyField({
  value,
  onChange,
  onAlliance,
  datalistId,
}: Props) {
  const [suggestions, setSuggestions] = useState<AirlineDTO[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onAllianceRef = useRef(onAlliance);
  onAllianceRef.current = onAlliance;
  const lastFilled = useRef('');

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const res = await apiSearchAirlines(q);
        setSuggestions(res);
        const exact = res.find((a) => a.name.toLowerCase() === q.toLowerCase());
        if (exact?.alliance && exact.alliance !== lastFilled.current) {
          lastFilled.current = exact.alliance;
          onAllianceRef.current(exact.alliance);
        }
      } catch {
        // offline or transient — suggestions just don't populate
      }
    }, 350);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [value]);

  return (
    <>
      <input
        className="form-input"
        list={datalistId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Lufthansa Miles & More"
      />
      <datalist id={datalistId}>
        {suggestions.map((a) => (
          <option key={a.iata || a.name} value={a.name}>
            {a.alliance ? `${a.name} — ${a.alliance}` : a.name}
          </option>
        ))}
      </datalist>
    </>
  );
}
